/**
 * Learn Chords Page
 *
 * Comprehensive guide to understanding and training chord recognition.
 * Covers theory, practical applications, and how to use the Chords Drill tool.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    LearnPageLayout,
    ContentSection,
    CalloutBox,
    OutcomeItem,
} from "../../components/learn";
import { ROUTES } from "../../config/routes";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import styles from "./StartHere.module.css";

const tocItems = [
    { id: "what-are-chords", label: "What are chords?" },
    { id: "why-chords-matter", label: "Why chords matter" },
    { id: "chord-types", label: "Common chord types" },
    { id: "chord-qualities", label: "Understanding chord qualities" },
    { id: "recognizing-chords", label: "Recognizing chords" },
    { id: "using-the-drill", label: "Using the chords drill" },
    { id: "practice-strategies", label: "Practice strategies" },
];

export default function ChordsLearn() {
    return (
        <>
            <SEO {...SEO_METADATA.LEARN_CHORDS} />
            <LearnPageLayout
                title="Learn Chords"
                subtitle="Master the foundation of harmony and musical color"
                tocItems={tocItems}
            >
            {/* What are Chords */}
            <ContentSection id="what-are-chords">
                <h2 className={styles.sectionTitle}>What are chords?</h2>

                <p>
                    A <strong>chord</strong> is three or more notes played together. That's the
                    simplest definition, but chords are so much more than that—they're the
                    emotional foundation of music.
                </p>

                <p>
                    Think about your favorite song. The melody might be what you hum, but the
                    <em> chords</em> are what make you feel something. They create the mood,
                    support the melody, and tell you whether a song is happy, sad, tense, or
                    triumphant.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Key insight:</strong> While intervals are about two notes, chords
                        are about relationships between <em>multiple</em> notes. They stack
                        intervals on top of each other to create rich, complex sounds. A Major
                        chord, for example, is built from a Major 3rd (4 semitones) plus a Minor
                        3rd (3 semitones) on top.
                    </p>
                </CalloutBox>

                <p>
                    Chords are built from a <strong>root note</strong> (the foundation) plus
                    specific intervals above that root. The particular intervals you choose
                    determine the chord's quality and emotional character.
                </p>
            </ContentSection>

            {/* Why Chords Matter */}
            <ContentSection id="why-chords-matter">
                <h2 className={styles.sectionTitle}>Why chords matter</h2>

                <p>
                    Understanding chords by ear is one of the most powerful skills a musician can
                    develop. When you can recognize chords, you unlock:
                </p>

                <div style={{ marginTop: "1.5rem" }}>
                    <OutcomeItem icon="🎸" title="Play songs by ear instantly">
                        Hear a song once and know exactly which chords to play on your instrument.
                    </OutcomeItem>
                    <OutcomeItem icon="🎹" title="Accompany yourself or others">
                        Provide harmonic support for singers or lead instruments without sheet
                        music.
                    </OutcomeItem>
                    <OutcomeItem icon="✍️" title="Write compelling music">
                        Understand which chords create tension, which bring resolution, and how to
                        craft emotional journeys in your compositions.
                    </OutcomeItem>
                    <OutcomeItem icon="🎵" title="Jam with confidence">
                        Jump into a jam session and instantly know what's being played, allowing
                        you to contribute meaningfully.
                    </OutcomeItem>
                    <OutcomeItem icon="🎼" title="Analyze and understand music">
                        Listen to your favorite artists and understand <em>why</em> their chord
                        choices sound so good.
                    </OutcomeItem>
                </div>
            </ContentSection>

            {/* Common Chord Types */}
            <ContentSection id="chord-types">
                <h2 className={styles.sectionTitle}>Common chord types</h2>

                <p>
                    There are many types of chords, but let's start with the most important ones.
                    Each chord type has a unique sound and emotional quality:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Triads (3-note chords)</h3>
                
                <div className={styles.chordTable}>
                    <table style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--text-color)", textAlign: "left" }}>
                                <th style={{ padding: "0.75rem" }}>Chord Type</th>
                                <th style={{ padding: "0.75rem" }}>Intervals</th>
                                <th style={{ padding: "0.75rem" }}>Example (C)</th>
                                <th style={{ padding: "0.75rem" }}>Sound/Mood</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Major 3rd, Perfect 5th</td>
                                <td style={{ padding: "0.75rem" }}>C, E, G</td>
                                <td style={{ padding: "0.75rem" }}>Happy, bright, stable</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Minor 3rd, Perfect 5th</td>
                                <td style={{ padding: "0.75rem" }}>C, E♭, G</td>
                                <td style={{ padding: "0.75rem" }}>Sad, dark, introspective</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Diminished</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Minor 3rd, Tritone</td>
                                <td style={{ padding: "0.75rem" }}>C, E♭, G♭</td>
                                <td style={{ padding: "0.75rem" }}>Tense, unstable, mysterious</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Augmented</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Major 3rd, Minor 6th</td>
                                <td style={{ padding: "0.75rem" }}>C, E, G♯</td>
                                <td style={{ padding: "0.75rem" }}>Dreamy, floating, unresolved</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Sus2</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Major 2nd, Perfect 5th</td>
                                <td style={{ padding: "0.75rem" }}>C, D, G</td>
                                <td style={{ padding: "0.75rem" }}>Open, ambiguous, anticipatory</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Sus4</strong></td>
                                <td style={{ padding: "0.75rem" }}>Root, Perfect 4th, Perfect 5th</td>
                                <td style={{ padding: "0.75rem" }}>C, F, G</td>
                                <td style={{ padding: "0.75rem" }}>Suspended, unresolved, anticipatory</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Seventh Chords (4-note chords)</h3>
                
                <div className={styles.chordTable}>
                    <table style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--text-color)", textAlign: "left" }}>
                                <th style={{ padding: "0.75rem" }}>Chord Type</th>
                                <th style={{ padding: "0.75rem" }}>Intervals</th>
                                <th style={{ padding: "0.75rem" }}>Example (C)</th>
                                <th style={{ padding: "0.75rem" }}>Sound/Mood</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major 7</strong></td>
                                <td style={{ padding: "0.75rem" }}>Major + Major 7th</td>
                                <td style={{ padding: "0.75rem" }}>C, E, G, B</td>
                                <td style={{ padding: "0.75rem" }}>Dreamy, jazzy, sophisticated</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor 7</strong></td>
                                <td style={{ padding: "0.75rem" }}>Minor + Minor 7th</td>
                                <td style={{ padding: "0.75rem" }}>C, E♭, G, B♭</td>
                                <td style={{ padding: "0.75rem" }}>Smooth, mellow, soulful</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Dominant 7</strong></td>
                                <td style={{ padding: "0.75rem" }}>Major + Minor 7th</td>
                                <td style={{ padding: "0.75rem" }}>C, E, G, B♭</td>
                                <td style={{ padding: "0.75rem" }}>Bluesy, tense, wants resolution</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Diminished 7</strong></td>
                                <td style={{ padding: "0.75rem" }}>Diminished + Diminished 7th</td>
                                <td style={{ padding: "0.75rem" }}>C, E♭, G♭, B♭♭</td>
                                <td style={{ padding: "0.75rem" }}>Very tense, dramatic, chromatic</td>
                            </tr>
                            <tr>
                                <td style={{ padding: "0.75rem" }}><strong>Half-Diminished 7</strong></td>
                                <td style={{ padding: "0.75rem" }}>Diminished + Minor 7th</td>
                                <td style={{ padding: "0.75rem" }}>C, E♭, G♭, B♭</td>
                                <td style={{ padding: "0.75rem" }}>Jazzy, complex, minor-ish</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <CalloutBox>
                    <p>
                        <strong>Don't be overwhelmed!</strong> Start with just Major and Minor
                        chords. They're in 90% of popular music. Once you can reliably hear the
                        difference between those two, gradually add others to your practice.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Understanding Chord Qualities */}
            <ContentSection id="chord-qualities">
                <h2 className={styles.sectionTitle}>Understanding chord qualities</h2>

                <p>
                    The "quality" of a chord comes from the intervals that make it up. Let's break
                    down the main categories:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Major vs. Minor: The 3rd makes all the difference</h3>
                <p>
                    The most fundamental distinction in chords is between <strong>major</strong>{" "}
                    and <strong>minor</strong>. The only difference? That third note—the 3rd
                    interval from the root:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Major 3rd</strong> (4 semitones) = bright, happy, positive
                    </li>
                    <li>
                        <strong>Minor 3rd</strong> (3 semitones) = dark, sad, introspective
                    </li>
                </ul>
                <p>
                    Both types have a Perfect 5th (7 semitones) as the top note, giving them
                    stability. This single semitone difference in the middle note is what creates
                    the dramatic emotional shift between major and minor.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Suspended chords: Neither major nor minor</h3>
                <p>
                    <strong>Sus2</strong> and <strong>Sus4</strong> chords replace the 3rd with a
                    2nd or 4th. Without that defining 3rd, they don't sound major or minor—they
                    sound <em>suspended</em>, waiting to resolve. They're common in modern pop and
                    rock for creating anticipation.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Diminished and augmented: Altering the 5th</h3>
                <p>
                    <strong>Diminished</strong> chords lower the 5th to a tritone (6 semitones),
                    creating maximum tension and instability. They appear in classical music,
                    jazz, and dramatic moments.
                </p>
                <p>
                    <strong>Augmented</strong> chords raise the 5th to a minor 6th (8 semitones),
                    creating a symmetrical, whole-tone sound that feels unresolved and dreamy.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Seventh chords: Adding color and complexity</h3>
                <p>
                    <strong>7th chords</strong> add a fourth note—a 7th interval above the root.
                    This extra note adds richness and complexity. The type of 7th determines the
                    chord's character:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Major 7th</strong> (11 semitones): Sophisticated, jazzy, dreamy
                    </li>
                    <li>
                        <strong>Minor 7th</strong> (10 semitones): The "blues" note—creates tension
                        and soul
                    </li>
                </ul>
                <p>
                    The <strong>Dominant 7</strong> chord (major triad + minor 7th) is
                    particularly important—it's the most common chord in blues and jazz, and it
                    creates strong tension that wants to resolve back to the tonic.
                </p>
            </ContentSection>

            {/* Recognizing Chords */}
            <ContentSection id="recognizing-chords">
                <h2 className={styles.sectionTitle}>How to recognize chords</h2>

                <p>
                    Recognizing chords by ear takes practice, but these strategies will
                    accelerate your learning:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Start with the emotional quality</h3>
                <p>
                    Before you try to identify specific chord types, ask yourself: How does this
                    chord <em>feel</em>?
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Happy/bright → probably <strong>Major</strong></li>
                    <li>Sad/dark → probably <strong>Minor</strong></li>
                    <li>Tense/unstable → likely <strong>Diminished</strong> or <strong>Dominant 7</strong></li>
                    <li>Dreamy/floating → could be <strong>Augmented</strong> or <strong>Major 7</strong></li>
                    <li>Ambiguous/open → might be <strong>Sus2</strong> or <strong>Sus4</strong></li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Listen for the intervals</h3>
                <p>
                    Once you've trained your ear for intervals (see the{" "}
                    <Link to={ROUTES.LEARN.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Intervals lesson
                    </Link>
                    ), you can break chords down into their component intervals:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Hear a Major 3rd? → Probably Major or Dominant 7</li>
                    <li>Hear a Minor 3rd? → Probably Minor or Diminished</li>
                    <li>Hear a tritone? → Look for Diminished or Dominant 7</li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Use reference songs</h3>
                <p>Just like with intervals, songs can be memory anchors for chord sounds:</p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li><strong>Major:</strong> First chord of "Let It Be" by The Beatles</li>
                    <li><strong>Minor:</strong> First chord of "Stairway to Heaven" by Led Zeppelin</li>
                    <li><strong>Dominant 7:</strong> Blues progressions, "Pride and Joy" by Stevie Ray Vaughan</li>
                    <li><strong>Major 7:</strong> "Isn't She Lovely" by Stevie Wonder</li>
                    <li><strong>Sus4:</strong> "Pinball Wizard" by The Who</li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Sing or hum the notes</h3>
                <p>
                    Try to sing each note of the chord individually. This helps you internalize
                    the sound and feel the relationships between the notes. Start with the lowest
                    note (the root), then move up through the chord.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Compare to a reference note</h3>
                <p>
                    If you have access to an instrument or pitch reference, play the root note,
                    then try to identify the intervals above it. This combines your interval
                    recognition skills with chord recognition.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Pro tip:</strong> The difference between Major and Minor is the
                        single most important distinction to master first. Focus intensely on that
                        one skill before moving to more complex chord types. Once you can hear
                        major vs. minor instantly, everything else becomes easier.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Using the Drill */}
            <ContentSection id="using-the-drill">
                <h2 className={styles.sectionTitle}>Using the chords drill</h2>

                <p>
                    Ready to train your ears? The{" "}
                    <Link to={ROUTES.DRILL.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Chords Drill
                    </Link>{" "}
                    is designed to help you recognize chord qualities quickly and confidently.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>How the drill works</h3>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Select your chords:</strong> Choose which chord types you want to
                        practice. Start with just Major and Minor, then expand as you improve.
                    </li>
                    <li>
                        <strong>Choose a practice mode:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li>
                                <strong>Questions mode:</strong> Practice a fixed number of
                                questions (e.g., 10 chords)
                            </li>
                            <li>
                                <strong>Time mode:</strong> Practice for a set duration (e.g., 60
                                seconds)
                            </li>
                            <li>
                                <strong>Endless mode:</strong> Keep going indefinitely for deep
                                practice sessions
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Start the drill:</strong> You'll hear a chord played (either all
                        notes together or one by one).
                    </li>
                    <li>
                        <strong>Identify the chord type:</strong> Select which chord quality you
                        heard from the available options.
                    </li>
                    <li>
                        <strong>Get instant feedback:</strong> The drill immediately tells you if
                        you're correct. If not, try again on the same chord.
                    </li>
                    <li>
                        <strong>Track your progress:</strong> See your score and accuracy at the
                        end of each session.
                    </li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Playback options</h3>
                <p>
                    Customize your practice with these settings:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Instrument:</strong> Choose from piano, guitar, strings, winds, and
                        more. Different timbres help you recognize chord qualities in various
                        contexts.
                    </li>
                    <li>
                        <strong>Play mode:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li>
                                <strong>Harmonic (simultaneous):</strong> All notes played at once—
                                the most common way to hear chords
                            </li>
                            <li>
                                <strong>Melodic (sequential):</strong> Notes played one at a time
                                from bottom to top—helps you hear each individual note and interval
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Volume:</strong> Adjust to your comfort level.
                    </li>
                    <li>
                        <strong>Replay button:</strong> Listen to the chord as many times as you
                        need before answering.
                    </li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Visual aid: Piano keyboard</h3>
                <p>
                    Toggle the piano keyboard display to see which notes are being played. This is
                    helpful when learning, as you can visualize the chord shapes and intervals. As
                    you progress, try practicing without the visual aid to develop pure aural
                    recognition.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Ready to start?</strong> Head to the{" "}
                        <Link to={ROUTES.DRILL.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chords Drill
                        </Link>{" "}
                        and begin training!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Practice Strategies */}
            <ContentSection id="practice-strategies">
                <h2 className={styles.sectionTitle}>Effective practice strategies</h2>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Master major and minor first</h3>
                <p>
                    This cannot be overstated: spend significant time—weeks if needed—drilling
                    just Major and Minor chords until you can distinguish them with 90%+ accuracy.
                    This is the foundation for everything else. Don't rush this step.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Add chord types gradually</h3>
                <p>Once you've mastered major/minor, follow this progression:</p>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Add <strong>Diminished</strong> (very distinctive sound)</li>
                    <li>Add <strong>Augmented</strong> (also very distinctive)</li>
                    <li>Add <strong>Dominant 7</strong> (essential for blues and jazz)</li>
                    <li>Add <strong>Major 7</strong> and <strong>Minor 7</strong></li>
                    <li>Finally add <strong>Sus2</strong>, <strong>Sus4</strong>, and more complex sevenths</li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Practice both playback modes</h3>
                <p>
                    <strong>Harmonic</strong> (simultaneous) playback is how you'll hear chords in
                    real songs, so focus there. But occasionally practice with{" "}
                    <strong>melodic</strong> (sequential) playback—it helps you hear the
                    individual intervals that make up each chord, strengthening your overall ear
                    training.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Vary your instruments</h3>
                <p>
                    Don't just practice with piano. Try guitar, strings, organ—different timbres
                    force your brain to focus on the harmonic relationships rather than the sound
                    quality. This makes your chord recognition more robust and transferable.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Apply it to real music</h3>
                <p>
                    Listen actively to songs you love and try to identify the chord qualities:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Is that chord major or minor?</li>
                    <li>Does it sound stable or tense?</li>
                    <li>Do I hear a 7th being added?</li>
                </ul>
                <p>
                    This real-world practice connects your drill work to actual music-making and
                    listening.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Daily consistency beats marathon sessions</h3>
                <p>
                    10 minutes every day is far better than 2 hours once a week. Your brain needs
                    regular, repeated exposure to build pattern recognition. Set a daily reminder
                    and make it a non-negotiable part of your routine.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>7. Focus on weak spots</h3>
                <p>
                    If you keep confusing Diminished and Minor, create a drill with just those
                    two. Targeted practice on your specific trouble areas yields rapid improvement.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>8. Play chords yourself</h3>
                <p>
                    After identifying a chord correctly, play it on your instrument. Sing through
                    the notes. This kinesthetic and vocal reinforcement deepens your understanding
                    and makes the sound stick in your memory.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Remember:</strong> Chord recognition is a skill that compounds over
                        time. Every practice session builds on the last. Be patient with yourself,
                        celebrate small wins, and trust that consistent practice will transform
                        your musical ear.
                    </p>
                </CalloutBox>

                <div style={{ marginTop: "3rem", padding: "1.5rem", background: "var(--callout-bg)", borderRadius: "8px" }}>
                    <h3 style={{ marginTop: 0 }}>Next Steps</h3>
                    <p style={{ marginBottom: "1rem" }}>
                        Now that you understand chords, start practicing:
                    </p>
                    <Link
                        to={ROUTES.DRILL.CHORDS}
                        style={{
                            display: "inline-block",
                            padding: "0.75rem 1.5rem",
                            background: "var(--accent-color)",
                            color: "white",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "600",
                            marginRight: "1rem",
                        }}
                    >
                        Start Chords Drill →
                    </Link>
                    <p style={{ marginTop: "1.5rem", marginBottom: 0 }}>
                        Or continue learning about{" "}
                        <Link to={ROUTES.LEARN.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chord Progressions
                        </Link>
                        , which combine chords into musical sequences.
                    </p>
                </div>
            </ContentSection>
        </LearnPageLayout>
        </>
    );
}
